import Reflux from 'reflux';
import UserActions from 'flux/actions/UserActions';
import RealTimeActions from 'flux/actions/RealTimeActions';
import _ from 'underscore';
import debugFactory from 'debug';
import TeamFormActions from 'flux/actions/TeamFormActions';
import ServerTimeActions from 'flux/actions/ServerTimeActions';
import ToastActions from 'flux/actions/ToastActions';
import { Map } from 'immutable';
import { datadogLogs } from '@datadog/browser-logs';
import socket from 'shared/lib/socket';
import TeamViewStore from 'flux/stores/TeamViewStore';
import CuesheetStore from 'flux/stores/cuesheet/CuesheetStore';
import FullScreenCuesheetStore from './cuesheet.fullscreen/FullScreenCuesheetStore';
import { isFullScreen } from 'main/components/show/cuesheet/cuesheetViewHelper';

import { getEventRoomId } from './SocketChannelStoreHelper';

let debug = debugFactory('shoflo:SocketChannelStore');

let onTeamCreated = TeamFormActions.submit.completed;

let cuesheetLoading = true;

export default Reflux.createStore({
  listenables: [UserActions, RealTimeActions],

  init() {
    this.listenTo(TeamViewStore, this.onTeamChange);
    this.listenTo(CuesheetStore, this.onCuesheetStoreChange);
    this.listenTo(FullScreenCuesheetStore, this.onFullScreenCuesheetStore);
  },

  onTeamChange(team) {
    this._teamId = team.get('_id');
  },

  onCuesheetStoreChange(cuesheet) {
    if (!isFullScreen()) {
      cuesheetLoading = cuesheet.get('loading');
    }
  },
  onFullScreenCuesheetStore({ loading }) {
    if (isFullScreen()) {
      cuesheetLoading = loading;
    }
  },

  onSetClientId(clientId) {
    this._clientId = clientId;

    this.listenTo(onTeamCreated, 'onTeamCreated');

    this._socket = socket.get();

    //Set up realTime message handler
    this._socket.on('realTime', (message) => {
      debug('realTime incoming message:', message);
      // don't process realtime messages if there is no cuesheet state
      if (cuesheetLoading) return;
      _processIncomingMessage(message, this._clientId);
    });

    // The 'connect' event is fired on both connection and reconnection
    this._socket.on('connect', () => {
      console.log('socket connected');
      if (this._socket.recovered) {
        console.log('socket connection state recovered');
        return;
      }
      this._subscribeToExistingRooms();
    });

    this._socket.on('disconnect', (reason, details) => {
      const context = {
        reason,
        description: details?.description,
        transport: this._socket.io.engine?.transport?.name,
      };
      console.log('socket disconnected', context);
      datadogLogs.logger.warn('socket disconnect', context);
    });

    this._socket.on('timeSync', (time) =>
      ServerTimeActions.syncServerTime(time),
    );

    this._rooms = [];
  },

  /**
   * Add new channel/room subscription when a new team is added
   *
   */
  onTeamCreated(team) {
    this._subscribe('team.' + team._id);
  },

  onSubscribe(roomId) {
    this._subscribe(roomId);
  },

  onUnsubscribe(roomId) {
    this._unsubscribe(roomId);
  },

  onSubscribeToShow(showId, context) {
    const roomId = `show.${showId}`;
    if (roomId.indexOf('undefined') >= 0) {
      debug(`Not subscribing to room ${roomId} because it is missing an ID.`);
      return;
    }
    this._subscribe(roomId, context);
  },

  onSubscribeToEvent(event) {
    const roomId = this._getEventRoomId(event);
    if (roomId.indexOf('undefined') >= 0) {
      debug(`Not subscribing to room ${roomId} because it is missing an ID.`);
      return;
    }

    this._subscribe(this._getEventRoomId(event));
  },

  onSubscribeToEvents(events) {
    events.forEach((event) => this.onSubscribeToEvent(event));
  },

  onSubscribeToTeam(team) {
    this._subscribe(this._getTeamRoomId(team));
  },

  onSubscribeToTeams(teams) {
    teams.forEach((team) => this.onSubscribeToTeam(team));
  },

  onSubscribeToUser(user) {
    this._subscribe(this._getUserRoomId(user));
  },

  onSubscribeToPrompterUser(userId, eventId, showId) {
    this._subscribe(`prompter.${userId}.${eventId}.${showId}`);
  },

  onUnsubscribeFromEvent(event) {
    this._unsubscribe(this._getEventRoomId(event));
  },

  onUnsubscribeFromEvents(events) {
    events.forEach((event) => this.onUnsubscribeFromEvent(event));
  },

  onUnsubscribeFromTeam(team) {
    this._unsubscribe(this._getTeamRoomId(team));
  },

  onUnsubscribeFromUser(user) {
    this._unsubscribe(this._getUserRoomId(user));
  },

  _getUserRoomId(user) {
    return 'user.' + (Map.isMap(user) ? user.get('_id') : user);
  },

  _getTeamRoomId(team) {
    return 'team.' + (Map.isMap(team) ? team.get('_id') : team);
  },

  _getEventRoomId(event) {
    return getEventRoomId(this._teamId, event);
  },

  onUnsubscribeFromTeams(teams) {
    teams.forEach((team) => this.onUnsubscribeFromTeam(team));
  },

  /**
   * Join a room
   */
  _subscribe(roomId, context) {
    const subscriptionIndex = this._rooms.findIndex(
      (subscription) => subscription.roomId == roomId,
    );
    if (subscriptionIndex >= 0) return;

    this._rooms.push({ roomId, context });
    if (this._socket.connected) {
      this._socket.emit('subscribe', { room: roomId, context });
    }
  },

  /**
   * Leave a room
   */
  _unsubscribe(roomId) {
    const indx = this._rooms.findIndex(
      (subscription) => subscription.roomId == roomId,
    );

    if (indx === -1) return;
    this._rooms.splice(indx, 1);
    if (this._socket.connected) {
      console.log('Socket unsubscribing from room', roomId);
      this._socket.emit('unsubscribe', roomId);
    }
  },

  _subscribeToExistingRooms() {
    console.log('socket subscribing to existing rooms');
    if (this._rooms.length) {
      this._rooms.forEach(({ roomId, context }) => {
        console.log('Socket subscribing to room', roomId);
        this._socket.emit('subscribe', { room: roomId, context });
      });
    }
  },
});

/**
 * Incoming realtime message handler
 *
 * @param raw
 * @param clientId
 * @private
 */
function _processIncomingMessage(message, clientId) {
  const {
    clientId: messageClientId,
    data,
    params,
    payload,
    responsePayload,
    tags,
  } = message;

  const clientTag = _.find(tags, (tag) => tag.indexOf('client:') >= 0),
    customTag = _.find(tags, (tag) => tag.indexOf('custom:') >= 0);

  if (messageClientId === clientId) {
    _selfClientMessageHandler(clientTag, message);
    return;
  }

  if (clientTag) {
    let clientMethod = clientTag.split(':')[1],
      handler = clientMessageHandlers[clientMethod];

    //Check if the handler exists and make sure the message isn't "stale"
    if (
      handler &&
      !_isDuplicate(message, clientMethod) &&
      !_isStale(message, clientMethod)
    ) {
      _cacheSequence(message, clientMethod);
      handler(params, payload, responsePayload, messageClientId);
    }
  }

  if (customTag) {
    let customNamespace = customTag.split(':')[1],
      handler = customMessageHandlers[customNamespace];

    //Check if the handler exists and make sure the message isn't "stale"
    if (
      handler &&
      !_isDuplicate(message, customNamespace) &&
      !_isStale(message, customNamespace)
    ) {
      _cacheSequence(message, customNamespace);
      handler(params, data);
    }
  }
}

/**
 * Handler for when the user originated the realtime action
 * @param clientTag
 * @returns {*}
 * @private
 */
function _selfClientMessageHandler(clientTag, message) {
  // Custom-only emits (tags: ['custom:...']) have no client tag, so
  // there's no operation type to evaluate for a toast.
  if (!clientTag) {
    debug('ignoring realtime message because it originated from this client');
    return;
  }
  //Only show toast if the operation had something to do w/ an itemSet
  if (clientTag.indexOf('itemSet') >= 0) {
    /**
     * Ignore certain fields that are often updated rapidly
     */
    if (message.payload && message.payload.changes) {
      let { presetDurationInSeconds, durationInSeconds, startOn } =
        message.payload.changes;
      if (typeof presetDurationInSeconds !== 'undefined') return;
      if (typeof durationInSeconds !== 'undefined') return;
      if (typeof startOn !== 'undefined') return;
    }

    return ToastActions.saved();
  }

  debug('ignoring realtime message because it originated from this client');
}

/**
 * custom namespace -> handler mappings
 */
const customMessageHandlers = {
  'itemSet.publishGlobalElementChanges': (
    params,
    payload,
    responsePayload,
    clientId,
  ) =>
    RealTimeActions.publishGlobalElementChangesRealTime(
      params,
      payload,
      responsePayload,
      clientId,
    ),
  'itemSet.publishShowRowChangesToGlobalElementRow': (
    params,
    payload,
    responsePayload,
  ) =>
    RealTimeActions.publishShowRowChangesToGlobalElementRowRealTime(
      params.showId,
      payload,
      responsePayload,
    ),
  'itemSet.removeGlobalElementReference': (params, payload, responsePayload) =>
    RealTimeActions.removeGlobalElementReferenceRealTime(
      params.showId,
      payload,
      responsePayload,
    ),
  'event.duplicateComplete': (params, payload, responsePayload) =>
    RealTimeActions.eventDuplicationCompletedRealtime(
      params,
      payload,
      responsePayload,
    ),
};

/**
 * client method -> handler mappings
 *
 */
var clientMessageHandlers = {
  /* eslint-disable no-unused-vars */
  'event.updatePhoto': (params, payload, responsePayload) =>
    RealTimeActions.eventUpdatePhotoRealTime(
      params.teamId,
      params.eventId,
      responsePayload,
    ),
  'event.update': (params, payload) =>
    RealTimeActions.eventMutatedRealTime(
      params.teamId,
      params.eventId,
      payload,
    ),
  'show.create': _handleShowCreate,
  'show.update': _handleShowUpdate,
  'schedule.create': _handleShowCreate,
  'schedule.update': _handleShowUpdate,
  'list.create': _handleShowCreate,
  'list.update': _handleShowUpdate,
  'show.recount': (params, payload, responsePayload) =>
    RealTimeActions.showRecountedRealTime(
      params.showId,
      payload,
      responsePayload,
    ),
  'show.resetAllStartTimes': (params, payload, responsePayload) =>
    RealTimeActions.showResetAllStartTimesRealTime(
      params.showId,
      payload,
      responsePayload,
    ),
  'event.create': (params, payload, responsePayload) =>
    RealTimeActions.eventCreatedRealTime(params.teamId, responsePayload),
  'event.createFromProduct': (params, payload, responsePayload) =>
    RealTimeActions.eventCreatedRealTime(params.teamId, responsePayload),
  'event.updateRole': (params, payload, responsePayload) =>
    RealTimeActions.eventUpdateRoleRealTime(params, payload),
  'eventDoc.create': (params, payload, responsePayload) =>
    RealTimeActions.eventCreateDocRealTime(
      params.teamId,
      params.eventId,
      responsePayload,
    ),
  'eventDoc.update': (params, payload, responsePayload) =>
    RealTimeActions.eventSaveDocChangesRealTime(
      params.teamId,
      params.eventId,
      params.eventDocId,
      responsePayload,
    ),
  'team.create': () => {}, //TODO..
  'team.updatePhoto': (params, payload, responsePayload) =>
    RealTimeActions.teamUpdatePhotoRealTime(params.teamId, responsePayload),
  'team.update': (params, payload) =>
    RealTimeActions.teamMutatedRealTime(params.teamId, payload),
  'itemSet.removeGlobalElementReferences': (params, payload, responsePayload) =>
    RealTimeActions.removeGlobalElementReferencesRealTime(
      params.showId,
      payload,
      responsePayload,
    ),
  'itemSet.publishLatestGlobalElementRowChangesToShowRow': (
    params,
    payload,
    responsePayload,
    clientId,
  ) =>
    RealTimeActions.publishLatestGlobalElementRowChangesToShowRowRealTime(
      params.showId,
      payload,
      responsePayload,
      clientId,
    ),
  // This app now emits updateRowLabels; updateRow stays mapped for peers on
  // older builds that still write labels through the legacy rows/{rowId}
  // endpoint. That endpoint is slated to be repurposed for broader row
  // updates, so accept only the labels-only shape older builds emit — any
  // other payload shape belongs to the repurposed endpoint and must be
  // ignored here rather than mis-applied as a labels update.
  'itemSet.updateRow': (params, payload, responsePayload) => {
    const payloadKeys = Object.keys(payload || {});
    const isLegacyLabelsShape =
      payloadKeys.length === 1 &&
      payloadKeys[0] === 'labels' &&
      Array.isArray(payload.labels) &&
      Boolean(responsePayload && responsePayload._id);

    if (!isLegacyLabelsShape) return;

    return RealTimeActions.rowUpdatedRealTime(
      params.showId,
      payload,
      responsePayload,
    );
  },
  'itemSet.updateRowLabels': (params, payload, responsePayload) =>
    RealTimeActions.rowUpdatedRealTime(params.showId, payload, responsePayload),
  'itemSet.updateMultipleItemRowCells': (
    params,
    payload,
    responsePayload,
    clientId,
  ) => {
    RealTimeActions.itemRowMultipleCellsMutatedRealTime(
      params.showId,
      payload,
      responsePayload,
      clientId,
    );
  },
  'itemSet.updateItemRowNote': (params, payload, responsePayload) =>
    RealTimeActions.itemRowNoteMutatedRealTime(
      params.showId,
      payload,
      responsePayload,
    ),
  'itemSet.restoreItemRow': (params, payload, responsePayload) =>
    RealTimeActions.itemRowRestoredRealTime(params, payload, responsePayload),
  'itemSet.createGlobalElementFromShowRow': (
    params,
    payload,
    responsePayload,
  ) =>
    RealTimeActions.createdGlobalElementFromShowRowRealTime(
      params,
      payload,
      responsePayload,
    ),
  'itemSet.createItemRowProof': (params, payload, responsePayload) =>
    RealTimeActions.itemRowProofAddedRealTime(
      params.showId,
      payload,
      responsePayload,
    ),
  'itemSet.clearItemRowProof': (params, payload, responsePayload) =>
    RealTimeActions.itemRowProofRemovedRealTime(
      params.showId,
      payload,
      responsePayload,
    ),
  'itemSet.updateItemRowProofNote': (params, payload, responsePayload) =>
    RealTimeActions.itemRowProofNoteUpdatedRealTime(
      params.showId,
      payload,
      responsePayload,
    ),
  'show.clearProofOfPerformance': (params, payload) =>
    RealTimeActions.showProofsClearedRealTime(params.showId, payload),
  'itemSet.clearItemRowProofHistory': (params, payload) =>
    RealTimeActions.itemRowProofHistoryClearedRealTime(params.showId, payload),
  'itemSet.createItemRows': (params, payload, responsePayload, clientId) =>
    RealTimeActions.itemRowsAddedRealTime(
      params.showId,
      payload,
      responsePayload,
      clientId,
    ),
  'itemSet.createGlobalElementItemRows': (
    params,
    payload,
    responsePayload,
    clientId,
  ) =>
    RealTimeActions.globalElementItemRowsAddedRealTime(
      params.showId,
      payload,
      responsePayload,
      clientId,
    ),
  'itemSet.softDeleteItemRow': (params, payload) =>
    RealTimeActions.itemRowDeletedRealTime(params.showId, payload),
  'itemSet.softDeleteItemRows': (params, payload, responsePayload, clientId) =>
    RealTimeActions.itemRowsDeletedRealTime(params.showId, payload, clientId),
  'itemSet.moveFromTo': (params, payload, responsePayload, clientId) =>
    RealTimeActions.itemRowMovedFromSourceToDestinationRealTime(
      params,
      payload,
      responsePayload,
      clientId,
    ),
  'itemSet.moveRows': (params, payload, responsePayload, clientId) =>
    RealTimeActions.itemRowsMovedRealTime(
      params.showId,
      payload,
      responsePayload,
      clientId,
    ),
  'itemSet.updateRowOrder': (params, payload, responsePayload, clientId) =>
    RealTimeActions.itemRowOrderUpdatedRealTime(
      params.showId,
      payload,
      responsePayload,
      clientId,
    ),
  'itemSetList.updateColumn': (params, payload, responsePayload, clientId) =>
    RealTimeActions.columnUpdatedRealTime(params.showId, payload, clientId),
  'itemSetList.setColumnOrderFromUser': (params, payload, responsePayload) =>
    RealTimeActions.setColumnOrderFromUserRealTime(
      params.showId,
      payload,
      responsePayload,
    ),
  'itemSet.updateItemRow': (params, payload, responsePayload, clientId) =>
    RealTimeActions.itemRowMutatedRealTime(
      params.showId,
      payload,
      responsePayload,
      clientId,
    ),
  'itemSetList.createColumn': (params, payload, responsePayload, clientId) =>
    RealTimeActions.itemSetListColumnCreatedRealTime(
      params.showId,
      payload,
      responsePayload,
      clientId,
    ),
  'itemSetList.restoreColumn': (params, payload, responsePayload, clientId) =>
    RealTimeActions.itemSetListColumnRestoredRealTime(
      params.showId,
      payload,
      responsePayload,
      clientId,
    ),
  'itemSetList.softDeleteColumn': (
    params,
    payload,
    responsePayload,
    clientId,
  ) =>
    RealTimeActions.itemSetListColumnDeletedRealTime(
      params.showId,
      payload,
      responsePayload,
      clientId,
    ),
  'itemSetList.updateItemSettings': (params, payload, responsePayload) =>
    RealTimeActions.itemSetListItemSettingsUpdatedRealTime(
      params.showId,
      payload,
    ),
  'itemSet.updateItemRowSettings': (
    params,
    payload,
    responsePayload,
    clientId,
  ) =>
    RealTimeActions.itemSetListItemRowSettingsUpdatedRealTime(
      params.showId,
      payload,
      clientId,
    ),
  'itemSet.deleteActualDurations': (params, payload, responsePayload) =>
    RealTimeActions.deleteActualDurationsRealTime(params, responsePayload),
  'user.updateTrackableCue': (params, payload, responsePayload) =>
    RealTimeActions.itemRowTrackedRealTime(
      params.userId,
      payload.showId,
      payload,
      responsePayload,
    ),
  'user.updateTrackableCueClock': (params, payload, responsePayload) =>
    RealTimeActions.cueClockMutatedRealTime(
      params.userId,
      payload.showId,
      payload,
    ),
  'user.updateTrackableCueAndCueClock': _handleTrackableCueAndCueClockUpdate,
  'user.clearTrackableCue': (params, payload, responsePayload, clientId) =>
    RealTimeActions.userRemovedTrackingRealTime(
      params,
      payload,
      responsePayload,
      clientId,
    ),
  'itemSet.updateItemRowPresentation': (params, payload, responsePayload) =>
    RealTimeActions.itemRowPresentationUpdatedRealTime(params.showId, payload),
  'itemSetList.moveColumn': (params, payload, responsePayload) =>
    RealTimeActions.itemSetListColumnMovedRealTime(
      params.showId,
      payload,
      responsePayload,
    ),
  'eventFileSystemObject.updateFolder': (params, payload, responsePayload) =>
    RealTimeActions.eventFolderUpdatedRealtime(params.eventId, payload),
  'eventFileSystemObject.createFolder': (params, payload, responsePayload) =>
    RealTimeActions.eventFolderCreatedRealtime(params.eventId, payload),
  'eventFileSystemObject.move': (params, payload, responsePayload) =>
    RealTimeActions.eventObjectMovedRealtime(params.eventId, payload),
  'eventFileSystemObject.moveToRoot': (params, payload, responsePayload) =>
    RealTimeActions.eventObjectMovedRealtime(params.eventId, payload),
  'eventFileSystemObject.archive': (params, payload, responsePayload) =>
    RealTimeActions.eventObjectArchivedRealtime(params.eventId, payload),
  'eventFileSystemObject.restoreArchive': (params, payload, responsePayload) =>
    RealTimeActions.eventObjectRestoredRealtime(params.eventId, payload),
  /* eslint-enable no-unused-vars */
};

function _handleShowCreate(params, payload, responsePayload) {
  RealTimeActions.showCreatedRealTime(
    params.teamId,
    params.eventId,
    responsePayload,
  );
}

// eslint-disable-next-line no-unused-vars
function _handleShowUpdate(params, payload, responsePayload, clientId) {
  RealTimeActions.showMutatedRealTime(params.showId, payload, clientId);
}

function _handleTrackableCueAndCueClockUpdate(
  params,
  payload,
  responsePayload,
) {
  const trackableCuePayload = _.omit(payload, 'cueClock');
  const cueClockPayload = payload.cueClock;

  RealTimeActions.itemRowTrackedRealTime(
    params.userId,
    payload.showId,
    trackableCuePayload,
    responsePayload,
  );

  RealTimeActions.cueClockMutatedRealTime(
    params.userId,
    payload.showId,
    cueClockPayload,
  );
}

/**
 * Cache message sequences so we can determine if something is "stale". Sometimes messages can jump in front of
 * one another, so they are out of sequence. Messages now have a sequence property (a timestamp) that determines their
 * sequence
 *
 * @type {{}}
 * @private
 */
const _sequenceCache = {};

/**
 * A list of methods that must be "fresh" (not "stale")
 * @type {string[]}
 * @private
 */
const _methodsThatCanBeStale = ['itemSet.moveRows', 'itemSet.updateRowOrder'];

const _methodCanBeStale = (clientMethod) =>
  _methodsThatCanBeStale.indexOf(clientMethod) >= 0;

/**
 * Check if a message is stale (for a give clientId + clientMethod combination)
 *
 * @param message
 * @param clientMethod
 * @private
 */
const _isStale = (message, clientMethod) => {
  return (
    _methodCanBeStale(clientMethod) &&
    _sequenceCache[message.clientId + clientMethod] > message.sequence
  );
};

/**
 * Check if a message is a duplicate (for a give clientId + clientMethod combination)
 *
 * @param message
 * @param clientMethod
 * @private
 */
const _isDuplicate = (message, clientMethod) => {
  return _sequenceCache[message.clientId + clientMethod] === message.sequence;
};

/**
 * Update the most recent sequence value for a clientId + clientMethod combination
 * @param message
 * @param clientMethod
 * @private
 */
const _cacheSequence = (message, clientMethod) =>
  (_sequenceCache[message.clientId + clientMethod] = message.sequence);
